import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { ArrowRight, Check, CircleDashed } from "lucide-react"

import { Skeleton } from "@/components/ui/skeleton"
import { useAuth } from "@/lib/auth"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import { fetchSalud, type LogroDeSalud, type SaludDelCierre } from "@/routes/finance/api"

/**
 * LA SALUD DEL CIERRE — lo único «gamificado» del producto, y hasta dónde.
 *
 * Cesar, 2026-10-04: «metiendo algo de gamificación». El brandbook pone el
 * límite: «una herramienta no se celebra a sí misma», sin confeti, sin emojis,
 * una sola animación intencional por pieza. Lo que queda dentro del límite es
 * esto: una cifra de 0 a 100 que SUBE cuando se hace el trabajo, seis hechos
 * comprobables con su siguiente paso, y una racha de meses cerrados. Es la
 * misma mecánica que hace volver a una app de hábitos, aplicada a lo que de
 * verdad hay que hacer cada mes para cerrar.
 *
 * La animación es UNA: el anillo se dibuja al llegar el dato (`stroke-dashoffset`
 * con transición). No parpadea, no pulsa, no celebra: muestra de dónde viene.
 *
 * Cada logro es un enlace a la pantalla donde se arregla. Una lista de
 * pendientes que no lleva a ningún sitio es un reproche; con el enlace es una
 * agenda.
 */
export function SaludCard({ className }: { className?: string }) {
  const { activeTenantId } = useAuth()
  const q = useQuery({
    queryKey: ["finance-salud", activeTenantId],
    queryFn: fetchSalud,
    enabled: Boolean(activeTenantId),
  })

  return (
    <section
      data-guia="salud"
      className={cn("rounded-panel border bg-surface shadow-xs", className)}
      aria-label={t("Salud del cierre")}
    >
      <div className="flex flex-wrap items-stretch gap-x-5 gap-y-3 p-3">
        <div className="flex items-center gap-3">
          <Anillo puntos={q.data?.puntos ?? null} nivel={q.data?.nivel ?? null} />
          <div className="min-w-0">
            <div className="eyebrow">{t("Salud del cierre")}</div>
            <div className="mt-0.5 text-sm font-medium leading-tight">
              {q.isLoading ? <Skeleton className="h-4 w-24" /> : q.data ? t(NIVEL[q.data.nivel]) : "—"}
            </div>
            {q.isLoading ? (
              <Skeleton className="mt-1 h-3 w-40" />
            ) : (
              <p className="mt-1 max-w-[34ch] text-2xs leading-4 text-muted-foreground">{q.data?.nota ?? ""}</p>
            )}
            {q.data && q.data.racha_meses > 0 ? (
              <p className="mt-1 text-2xs text-muted-foreground">
                <span className="num-strong">{q.data.racha_meses}</span>{" "}
                {q.data.racha_meses === 1 ? t("mes cerrado seguido") : t("meses cerrados seguidos")}
              </p>
            ) : null}
          </div>
        </div>

        <ul className="grid min-w-0 flex-1 gap-x-4 gap-y-1 self-center sm:grid-cols-2 xl:grid-cols-3">
          {q.isLoading
            ? Array.from({ length: 6 }).map((_, i) => <li key={i}><Skeleton className="h-6 w-full" /></li>)
            : (q.data?.logros ?? []).map((l) => (
                <Logro key={l.clave} logro={l} siguiente={q.data?.siguiente === l.clave} />
              ))}
        </ul>
      </div>
    </section>
  )
}

const NIVEL: Record<SaludDelCierre["nivel"], string> = {
  al_dia: "Al día",
  casi: "Casi al día",
  con_pendientes: "Con pendientes",
}

function Logro({ logro, siguiente }: { logro: LogroDeSalud; siguiente: boolean }) {
  return (
    <li>
      <Link
        to={logro.a}
        title={logro.detalle}
        className={cn(
          "group flex items-center gap-2 rounded-md px-1.5 py-1 text-xs leading-tight",
          "transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          logro.logrado ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {logro.logrado ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
        ) : (
          <CircleDashed
            className="h-3.5 w-3.5 shrink-0"
            style={siguiente ? { color: "hsl(var(--margen))" } : undefined}
            aria-hidden="true"
          />
        )}
        <span className={cn("min-w-0 flex-1 truncate", logro.logrado && "line-through decoration-border-strong")}>
          {t(logro.titulo)}
        </span>
        {!logro.logrado ? (
          <ArrowRight className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
        ) : null}
      </Link>
    </li>
  )
}

/**
 * El anillo. Radio 26, trazo 4: el arco mide 2π·26 ≈ 163. El relleno no lleva
 * color de alerta nunca — el rojo de esta pantalla ya lo tiene «lo que requiere
 * atención» —, cambia de gris a verde libro al pasar de «con pendientes».
 */
function Anillo({ puntos, nivel }: { puntos: number | null; nivel: SaludDelCierre["nivel"] | null }) {
  const r = 26
  const largo = 2 * Math.PI * r
  const [dibujado, setDibujado] = React.useState(false)
  React.useEffect(() => {
    if (puntos == null) return
    const id = window.requestAnimationFrame(() => setDibujado(true))
    return () => window.cancelAnimationFrame(id)
  }, [puntos])
  const fraccion = dibujado && puntos != null ? Math.max(0, Math.min(1, puntos / 100)) : 0
  const color = nivel === "con_pendientes" || nivel == null
    ? "hsl(var(--muted-foreground))"
    : "hsl(var(--chart-1))"

  return (
    <div className="relative h-16 w-16 shrink-0" role="img"
         aria-label={puntos == null ? t("Calculando") : `${puntos} ${t("de 100")}`}>
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="4" />
        <circle
          cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={largo}
          strokeDashoffset={largo * (1 - fraccion)}
          style={{ transition: "stroke-dashoffset 900ms cubic-bezier(0.22, 1, 0.36, 1), stroke 300ms" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span className="cifra text-lg leading-none">{puntos == null ? "…" : puntos}</span>
      </div>
    </div>
  )
}
